import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'redaction'
import actions from 'redux/actions'
import Media from 'sb-media'
import { constants, getMergedProductAndVolume } from 'common/helpers'
import { FormattedMessage } from 'sb-react-intl'

import Link from 'components/ui/Link/Link'
import Modal from 'components/modal/Modal/Modal'
import Button from 'components/buttons/Button/Button'
import { AddToQueueOrCartButton } from 'components/_new/buttons/addTo'
import RatingStars from 'components/rating/RatingStars/RatingStars'
import Volumes from 'components/product/ProductVolumes/ProductVolumes'
import ProductNotes from 'components/product/ProductNotes/ProductNotes'
import ProductImage from 'components/product/ProductImage/ProductImage'
import ProductGender from 'components/product/ProductGender/ProductGender'
import ProductCounter from 'components/product/ProductCounter/ProductCounter'
import HintForPremium from 'components/product/HintForPremium/HintForPremium'
import OutOfStockText from 'components/product/OutOfStockText/OutOfStockText'
import ActiveVolumeInfo from 'components/product/ActiveVolumeInfo/ActiveVolumeInfo'
import StockNotifyButton from 'components/product/StockNotifyButton/StockNotifyButton'
import ContentWithLoader from 'components/loaders/ContentWithLoader/ContentWithLoader'

import Tags from './Tags/Tags'

import messages from './messages'
import s from './ProductDetailsModal.scss'


const testIds = {
  name: 'name',
  brand: 'brand',
  type: 'type',
}

class ProductDetailsModal extends Component {

  static propTypes = {
    name: PropTypes.string,
    activeVolume: PropTypes.object,
    modalData: PropTypes.object,
    data: PropTypes.shape({
      productId: PropTypes.number,
      defaultSelectedQuickViewType: PropTypes.string,
    }),
  }

  static defaultProps = {
    name: constants.modals.ProductDetails,
    info: {
      labels: [],
    },
  }

  componentDidMount() {
    const { data: { productId } } = this.props

    actions.view.productDetailsModal.fetch(productId)
  }

  componentWillUnmount() {
    this.handleResetData()
  }

  handleResetData = () => {
    actions.view.productDetailsModal.reset()
  }

  handleRedirectClick = () => {
    const { name } = this.props

    actions.view.productPage.setSource('quick view')
    actions.models.modals.close(name)
  }

  handleOnAfterAdd = () => {
    actions.view.productDetailsModal.setActiveVolumeIndex(0)
    actions.models.modals.close(constants.modals.ProductDetails)
  }

  render() {
    const { name: modalName, activeVolume, modalData: { isFetching, info } } = this.props

    const {
      url, brand, name, category, img, rate, rateCount, notes, volumes, tagTypes, labels,
      limitedEditionProductsRemainingCount, isSubscribedToStockNotification, isVisible,
      isLimitedEdition: isLimitedEditionProduct, limitedEditionVolumeCustomText, upchargePrice,
      typeGroup, sex, isGenderVisible, id,
    } = info
    let { tags } = info

    const hasData                   = info && info.id
    const isTagTypes                = Boolean(tagTypes)
    const isLEPOutOfStockProduct    = labels.some((label) => /limited-edition-sold-out/.test(label))
    const isCream                   = /\/cream/.test(img)
    const isExtra                   = [
      constants.productTypeGroups.perfumeCase,
      constants.productTypeGroups.candleSet,
      constants.productTypeGroups.fragranceStorageBox,
    ].indexOf(typeGroup) > -1

    if (isTagTypes) {
      tags = tagTypes.map(({ topTagImage, name, topTagName }) => ({
        img: topTagImage,
        type: name,
        name: topTagName,
      }))
    }
    else {
      // get rid of unneccessary images
      tags = tags.map(({ img, ...rest }) => ({ ...rest }))
    }

    const linkButtonProps = {
      to: url,
      onClick: this.handleRedirectClick,
    }

    const stockNotifyButtonProps = {
      productId: info.id,
      isSubscribedToStockNotification,
      onSubscribeToStockNotification: actions.view.productDetailsModal.handleStockNotificationSubscriptionSuccess,
    }

    const addProductProps = {
      addTo: activeVolume ? activeVolume.type === 'subscription' ? 'queue' : 'cart' : null,
      product: getMergedProductAndVolume(info, activeVolume, isExtra),
      metadata: {
        placement: 'quick view',
      },
      snowplowMetadata: {
        placement: 'quick view',
      },
      onAfterAdd: this.handleOnAfterAdd,
    }

    const productImageSrc = (isExtra && activeVolume && activeVolume.img) ? activeVolume.img : img

    const productImageProps = {
      key: url,
      labels,
      src: productImageSrc,
      link: url,
      upchargePrice,
      onClick: this.handleRedirectClick,
    }

    const imageNode = (
      <ProductImage {...productImageProps} w={300} />
    )

    const volumesNode = (() => {
      if (isLimitedEditionProduct) {
        return (
          <ProductCounter count={limitedEditionProductsRemainingCount} />
        )
      }
      if (isVisible) {
        return (
          <Volumes
            className={s.volumes}
            items={volumes}
            activeItem={activeVolume}
            onSelect={(index) => actions.view.productDetailsModal.setActiveVolumeIndex(index)}
          />
        )
      }
    })()

    const notesNode = (
      <ProductNotes className={s.notes} noteClassName={s.note} items={notes} hasTitle />
    )

    return (
      <Modal
        className={s.fragranceDetailsModal}
        name={modalName}
        onClose={this.handleResetData}
      >
        <ContentWithLoader isFetching={isFetching}>
          {
            hasData && (
              <Media mobile>
                {
                  (isMobile) => isMobile ? (
                    <Fragment>
                      <Link to={url} styleless onClick={this.handleRedirectClick}>
                        <div className={s.brand} data-testid={testIds.brand}>{brand}</div>
                        {
                          isExtra ? (
                            <div className={s.category} data-testid={testIds.type}>{category}</div>
                          ) : (
                            <div className={s.name} data-testid={testIds.name}>{name}</div>
                          )
                        }
                      </Link>
                      {
                        isGenderVisible && (
                          <ProductGender sex={sex} />
                        )
                      }
                      {
                        Boolean(upchargePrice) && (
                          <HintForPremium typeGroup={typeGroup} placement="quick view" />
                        )
                      }
                      <div className={s.rating}>
                        <RatingStars className={s.ratingStars} lg value={rate} />
                        <FormattedMessage
                          className={s.ratingTitle}
                          tag="div"
                          {...messages.rateCountText}
                          values={{ rateCount }}
                        />
                      </div>
                      {imageNode}
                      {
                        isLimitedEditionProduct && (
                          <ProductCounter count={limitedEditionProductsRemainingCount} />
                        )
                      }
                      {
                        !isVisible && (
                          <OutOfStockText className={s.outOfStock} />
                        )
                      }
                      {
                        isLEPOutOfStockProduct ? (
                          <StockNotifyButton fullWidth {...stockNotifyButtonProps} />
                        ) : (
                          (isVisible && addProductProps.addTo) && (
                            <AddToQueueOrCartButton
                              disabled={!activeVolume.inStock}
                              fullWidth
                              brand
                              md
                              dataTestId={constants.testIds.payment.addProductButton}
                              {...addProductProps}
                            />
                          )
                        )
                      }
                      <Button
                        className={s.linkButton}
                        uppercase
                        loading={isFetching}
                        title={messages.linkButtonTitle}
                        fullWidth
                        white
                        md
                        {...linkButtonProps}
                      />
                      <FormattedMessage className={s.text} tag="div" {...messages.text} values={{ isCream }} />
                      {notesNode}
                    </Fragment>
                  ) : (
                    <div className={s.content}>
                      <div className={s.col}>
                        {imageNode}
                        <Tags items={tags} lightGray={isTagTypes} />
                      </div>
                      <div className={s.col}>
                        <Link to={url} styleless onClick={this.handleRedirectClick}>
                          <div className={s.brand} data-testid={testIds.brand}>{brand}</div>
                          {
                            !isExtra && (
                              <div className={s.name} data-testid={testIds.name}>{name}</div>
                            )
                          }
                          <div className={s.category} data-testid={testIds.type}>{category}</div>
                        </Link>
                        {
                          isGenderVisible && (
                            <ProductGender sex={sex} />
                          )
                        }
                        {
                          Boolean(upchargePrice) && (
                            <HintForPremium typeGroup={typeGroup} placement="quick view" />
                          )
                        }
                        <div className={s.innerDescription}>
                          {isExtra && volumesNode}
                          {
                            isVisible ? (
                              <ActiveVolumeInfo
                                className={s.activeVolumeInfo}
                                data={{ limitedEditionVolumeCustomText, upchargePrice, ...activeVolume }}
                              />
                            ) : (
                              <OutOfStockText className={s.outOfStock} />
                            )
                          }
                          {
                            !isLEPOutOfStockProduct && isVisible && addProductProps.addTo && (
                              <AddToQueueOrCartButton
                                className={s.addProductButton}
                                disabled={!activeVolume.inStock}
                                fullWidth
                                brand
                                xlg
                                dataTestId={constants.testIds.payment.addProductButton}
                                {...addProductProps}
                              />
                            )
                          }
                          {!isExtra && volumesNode}
                          {
                            isLEPOutOfStockProduct && (
                              <StockNotifyButton fullWidth {...stockNotifyButtonProps} />
                            )
                          }
                          {notesNode}
                        </div>
                      </div>
                    </div>
                  )
                }
              </Media>
            )
          }
        </ContentWithLoader>
      </Modal>
    )
  }
}


export default connect({
  modalData: 'view.productDetailsModal',
  activeVolume: (state, props) => {
    const { data: { defaultSelectedQuickViewType } } = props

    return actions.view.productDetailsModal.getActiveVolume(defaultSelectedQuickViewType)
  },
})(ProductDetailsModal)
